import {
  Purchases,
  LOG_LEVEL,
  PurchasesStoreProduct,
  PurchasesOfferings,
} from '@revenuecat/purchases-capacitor';
import { AppsFlyer } from 'appsflyer-capacitor-plugin';
import { Capacitor } from '@capacitor/core';
import { getUserIdFromJWT } from '../utils/jwtUtils';

export interface StoreKitProduct {
  productId: string;
  localizedTitle?: string;
  localizedDescription?: string;
  price?: string;
  priceNumber?: number; // Raw numeric price for calculations
  pricePerMonthString?: string | null;
  currencyCode?: string;
  rcPackageIdentifier?: string; // e.g. "monthly", "platinum_monthly"
  introPrice?: {
    price: number;
    priceString: string;
    periodUnit: string; // DAY, WEEK, MONTH, YEAR
    periodNumberOfUnits: number;
    cycles: number;
  } | null;
}

export interface InitStoreKitResult {
  available: boolean;
  products: StoreKitProduct[];
  reason?: string;
}

const isNative = Capacitor.isNativePlatform();

const unavailableReason = !isNative
  ? 'In-app purchases require a native (iOS/Android) build.'
  : 'RevenueCat Purchases plugin not found. Did you install @revenuecat/purchases-capacitor?';

const waitForDeviceReady = (() => {
  if (!isNative || typeof document === 'undefined' || typeof window === 'undefined') {
    return Promise.resolve();
  }

  let resolved = false;
  const maybeResolve = (resolve: () => void) => {
    if (!resolved && ((window as any)?.cordova || (window as any)?.Capacitor)) {
      resolved = true;
      resolve();
    }
  };

  return new Promise<void>((resolve) => {
    maybeResolve(resolve);
    if (resolved) return;

    const handler = () => {
      if (!resolved) {
        resolved = true;
        document.removeEventListener('deviceready', handler);
        resolve();
      }
    };

    document.addEventListener('deviceready', handler, { once: true });
    setTimeout(() => maybeResolve(resolve), 0);
  });
})();

let purchasesConfigured = false;
const productCache: Record<string, PurchasesStoreProduct> = {};

function getRevenueCatApiKey(): string | undefined {
  const platform = Capacitor.getPlatform();
  const iosKey =
    import.meta.env?.VITE_REVENUECAT_IOS_API_KEY || (globalThis as any)?.process?.env?.VITE_REVENUECAT_IOS_API_KEY;
  const androidKey =
    import.meta.env?.VITE_REVENUECAT_ANDROID_API_KEY || (globalThis as any)?.process?.env?.VITE_REVENUECAT_ANDROID_API_KEY;

  if (platform === 'ios') {
    return iosKey;
  }
  if (platform === 'android') {
    return androidKey;
  }
  return iosKey || androidKey;
}

async function ensurePurchasesConfigured() {
  if (!isNative) return;

  const apiKey = getRevenueCatApiKey();
  if (!apiKey) {
    throw new Error(
      'Missing RevenueCat API key. Set VITE_REVENUECAT_IOS_API_KEY and/or VITE_REVENUECAT_ANDROID_API_KEY.',
    );
  }

  await Purchases.setLogLevel?.({ level: LOG_LEVEL.DEBUG });

  // Get User ID from JWT Token
  const userId = getUserIdFromJWT();
  if (!userId) {
    throw new Error('Missing user ID from JWT token. Please login first.');
  }
  const appUserID = userId;

  if (!purchasesConfigured) {
    await Purchases.configure({ apiKey: apiKey, appUserID });
    purchasesConfigured = true;
  } else if (userId) {
    // If already configured but we now have a userId (e.g. after login), log in.
    try {
      await Purchases.logIn({ appUserID: userId });
    } catch (e) {
      console.error('[IAP] Failed to log in to RevenueCat:', e);
    }
  }

  // Set AppsFlyer ID only after Purchases is configured.
  try {
    const afUid = await AppsFlyer.getAppsFlyerUID();
    if (afUid?.uid) {
      await Purchases.setAppsflyerID({ appsflyerID: afUid.uid });
      console.log('[IAP] AppsFlyer ID set for RevenueCat:', afUid.uid);
    }
  } catch (e) {
    console.warn('[IAP] Failed to set AppsFlyer ID:', e);
  }
}

function cacheProducts(products: PurchasesStoreProduct[]) {
  products.forEach((product) => {
    if (product?.identifier) {
      productCache[product.identifier] = product;
    }
  });
}

// Return tuple of [Product, Identifier]
function collectStoreProducts(offerings: PurchasesOfferings | null | undefined): { product: PurchasesStoreProduct, identifier: string }[] {
  if (!offerings) return [];

  const items: { product: PurchasesStoreProduct, identifier: string }[] = [];

  // Include current offering packages
  const currentPackages = offerings.current?.availablePackages ?? [];
  currentPackages.forEach((pkg: any) => {
    if (pkg?.product) {
      items.push({
        product: pkg.product as PurchasesStoreProduct,
        identifier: pkg.identifier
      });
    }
  });

  return items;
}

function adaptProduct(item: { product: PurchasesStoreProduct, identifier: string }): StoreKitProduct {
  const { product, identifier } = item;
  return {
    productId: product.identifier,
    localizedTitle: product.title,
    localizedDescription: product.description,
    price: product.priceString ?? (typeof product.price === 'number' ? `$${product.price.toFixed(2)}` : undefined),
    priceNumber: product.price,
    pricePerMonthString: product.pricePerMonthString ?? null,
    currencyCode: product.currencyCode,
    rcPackageIdentifier: identifier,
    introPrice: product.introPrice ? {
      price: product.introPrice.price,
      priceString: product.introPrice.priceString,
      periodUnit: product.introPrice.periodUnit,
      periodNumberOfUnits: product.introPrice.periodNumberOfUnits,
      cycles: product.introPrice.cycles,
    } : null,
  };
}

export async function initStoreKit(): Promise<InitStoreKitResult> {
  if (!isNative) {
    return {
      available: false,
      products: [],
      reason: unavailableReason,
    };
  }

  try {
    await waitForDeviceReady;
    await ensurePurchasesConfigured();

    const offerings = await Purchases.getOfferings();
    const storeItems = collectStoreProducts(offerings);

    cacheProducts(storeItems.map(i => i.product));
    const adapted = storeItems.map(adaptProduct);

    if (!adapted.length) {
      return {
        available: false,
        products: [],
        reason: 'No matching products were returned by RevenueCat.',
      };
    }

    return { available: true, products: adapted };
  } catch (error) {
    console.error('[IAP] Failed to initialize RevenueCat', error);
    return {
      available: false,
      products: [],
      reason:
        (error as Error)?.message ||
        'Failed to initialize in-app purchases. Please try again.',
    };
  }
}

export async function purchaseProduct(productId: string) {
  if (!isNative) {
    throw new Error(unavailableReason);
  }
  await waitForDeviceReady;
  await ensurePurchasesConfigured();

  let product = productCache[productId];
  if (!product) {
    const offerings = await Purchases.getOfferings();
    const storeItems = collectStoreProducts(offerings);
    cacheProducts(storeItems.map(i => i.product));
    product = productCache[productId];
  }

  if (!product) {
    throw new Error(`Product ${productId} not found in RevenueCat.`);
  }

  return Purchases.purchaseStoreProduct({ product });
}

export async function getCustomerInfo() {
  if (!isNative) {
    return null;
  }
  try {
    await waitForDeviceReady;
    await ensurePurchasesConfigured();
    const result = await Purchases.getCustomerInfo();
    return result.customerInfo;
  } catch (error) {
    console.error('[IAP] Failed to get customer info', error);
    return null;
  }
}

export function isStoreKitAvailable() {
  return isNative;
}
